/**
 * DataModel entity — describes the semantic layer that powers the analytics
 * handler. Stored in DynamoDB table `leviathan-datamodels`, keyed by
 * `tenantId` + `version`. Each tenant gets one active DataModel derived from
 * its vertical template (see `shared/src/data-model/templates.ts`).
 *
 * A DataModel is the contract between the analytics SQL builder and the
 * frontend: facts define the measurable grains (orders, subscriptions,
 * appointments, …); dimensions define the attributes available for grouping
 * and filtering; joins connect facts to dimensions via foreign keys.
 */
import { z } from 'zod';
import { Vertical, ValueFormat } from '../enums/index.js';
/** An attribute of a dimension — a column that can be selected / filtered. */
export const DimensionAttributeSchema = z.object({
    name: z.string().min(1),
    label: z.string().min(1),
    /** Logical type. `geo` attributes participate in map-based visualizations. */
    type: z.enum(['string', 'number', 'date', 'boolean', 'geo']),
    /** Optional display formatter hint for the frontend. */
    format: ValueFormat.optional(),
    /** When true, the attribute can be used in the filter UI. Default: true. */
    filterable: z.boolean().optional(),
    /** When true, the attribute can be used as a grouping axis. Default: true. */
    groupable: z.boolean().optional(),
    /** Optional description shown as tooltip in the UI. */
    description: z.string().optional(),
});
/** A dimension — a table of descriptive attributes joined to facts. */
export const DimensionSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    /** Physical MySQL table name within the tenant schema. */
    table: z.string().min(1),
    /** Primary-key column on the dimension table. */
    key: z.string().min(1),
    attributes: z.array(DimensionAttributeSchema).min(1),
    /** Optional short description surfaced in the catalog UI. */
    description: z.string().optional(),
});
/** A measure — an aggregable numeric quantity on a fact. */
export const MeasureSchema = z.object({
    name: z.string().min(1),
    label: z.string().min(1),
    /** Aggregation function used by the SQL builder. */
    type: z.enum(['sum', 'count', 'count_distinct', 'avg', 'min', 'max']),
    /**
     * Physical column name on the fact table. Omitted for pure-count measures
     * where the `type` is `count` of `*`.
     */
    column: z.string().optional(),
    format: z.enum(['currency', 'percentage', 'number']),
    /** Optional description surfaced in the catalog UI. */
    description: z.string().optional(),
});
/** A join — how a fact connects to a dimension via a foreign key. */
export const JoinSchema = z.object({
    /** Foreign-key column on the fact table. */
    fk: z.string().min(1),
    type: z.enum(['inner', 'left', 'right']),
});
/** A fact — a grain of measurable events (orders, sessions, appointments…). */
export const FactSchema = z.object({
    id: z.string().min(1),
    label: z.string().min(1),
    /** Physical MySQL table name within the tenant schema. */
    table: z.string().min(1),
    /** Grain: ordered list of columns that uniquely identify a row. */
    grain: z.array(z.string().min(1)).min(1),
    measures: z.array(MeasureSchema).min(1),
    /** Map of dimensionId → join description. */
    joins: z.record(z.string(), JoinSchema),
    /** Optional description surfaced in the catalog UI. */
    description: z.string().optional(),
    /**
     * Optional list of daily/periodic fact tables that can be used as
     * pre-aggregated sources when the query granularity matches. The analytics
     * handler prefers these over the raw grain for performance.
     */
    preAggregated: z
        .array(z.object({
        table: z.string().min(1),
        granularity: z.enum(['day', 'week', 'month']),
    }))
        .optional(),
});
/** A full DataModel document. */
export const DataModelSchema = z.object({
    version: z.number().int().positive(),
    vertical: Vertical,
    /** Feature toggles consumed by the frontend navigation/catalog. */
    features: z.record(z.string(), z.boolean()).optional(),
    /** Map of factId → Fact definition. */
    facts: z.record(z.string(), FactSchema),
    /** Map of dimensionId → Dimension definition. */
    dimensions: z.record(z.string(), DimensionSchema),
});
//# sourceMappingURL=data-model.js.map